<template>
  <BaseModule
    v-bind="$props"
    class="widget-light"
    data-module="widget-light"
  >
    <div
      v-if="!isMobile"
      key="widget__desk"
      class="pmweb_widget sweet"
      data-best-rates-color="white"
      data-destinations-list="true"
      :data-lang="apiLocale"
      data-opacity="100"
      data-tpl="1|light"
      :data-first-color="backgroundColor"
      :data-second-color="textColor"
      :data-third-color="buttonColor || settings.primaryColor"
      :data-idlb="settings.letsbookApiId"
      :data-url="widgetUrl"
      :data-option-selected="defaultDestination"
      :data-show-rates="`${showPrices}`"
    ></div>
    <div
      v-else
      key="widget-light__mobile"
      class="widget-light__mobile"
    >
      <EditorLink
        :model="mobileUrl"
        class="cta"
      >
        {{ buttonText }}
      </EditorLink>
    </div>
  </BaseModule>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import debounce from 'lodash/debounce';

import EditorLink from './EditorLink.vue';
import BaseModule from './generic/BaseModule.vue';

export default {
  name: 'WidgetLight',
  components: {
    BaseModule,
    EditorLink,
  },
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    defaultSelectedDestination: {
      type: [Number, String],
      default: null,
    },
    useDestinationOrHotel: Boolean,
    showPrices: {
      type: Boolean,
      default: true,
    },
    buttonText: {
      type: String,
      default: 'Reservar',
    },
    backgroundColor: {
      type: String,
      default: '#ffffff', // overrides default
    },
    textColor: {
      type: String,
      default: '#474747', // overrides default
    },
  },
  data() {
    return {
      currentScript: null,
      destinations: [],
      isMobile: null,
      mobileUrl: '',
    };
  },
  computed: {
    widgetUrl() {
      return this.settings.letsbookUrl?.replace(/\/admin\/?$/i, '');
    },
    scriptExists() {
      return !!this.currentScript;
    },
    defaultDestination() {
      if (this.useDestinationOrHotel) {
        return this.defaultSelectedDestination;
      }
      return '';
    },
    apiLocale() {
      switch (this.$i18n.locale) {
        case 'en':
          return 'en-us';
        case 'es':
          return 'es-es';
        default:
          return 'pt-br';
      }
    },
    ...mapState(['customer']),
    ...mapGetters(['settings']),
  },
  watch: {
    $props: {
      handler() {
        this.reloadScript();
      },
      deep: true,
    },
    settings: {
      handler() {
        this.reloadScript();
      },
      deep: true,
    },
    isMobile: {
      handler(value) {
        if (!value) {
          this.addScript();
        } else {
          this.removeScript();
        }
      },
    },
  },
  created() {
    if (this.settings.letsbookApiId) {
      this.fetchDestinations();
    }
  },
  mounted() {
    this.addScript();
    this.onLayoutChange();
    window.addEventListener('resize', debounce(this.onLayoutChange, 150));

    if (!this.settings.letsbookApiId) {
      this.logError({
        code: 'site-widget-integration',
        customer: this.customer,
        url: window?.location.href || 'server',
        idLets: this.settings.letsbookApiId,
      });
      const errorMsg =
        'A integração com o Motor Letsbook não está configurada.';
      console.warn(errorMsg);
      return;
    }

    const url = `https://www.pmweb.com.br/apiwidget/json/${this.settings.letsbookApiId}/`;

    fetch(url)
      .then((res) => res.json())
      .then(({ Dados: { InstanciaLets } = {} } = {}) => {
        const letsParams = new URLSearchParams();

        if (this.defaultDestination && /^\d+$/.test(this.defaultDestination)) {
          // no buscador não existe parâmetro para o destino, apenas hotel (id numéricos)
          letsParams.set('hotel', this.defaultDestination);
        }

        this.mobileUrl = `https://${InstanciaLets}/reserva/busca?${letsParams.toString()}`;
      });
  },
  beforeDestroy() {
    this.removeScript();
  },
  methods: {
    fetchDestinations() {
      const api =
        this.settings.letsbookApiId === ('HOMO1' || 'HOMO2' || 'HOMO3')
          ? 'https://homologa1.letsbook.com.br/api'
          : 'https://api.letsbook.com.br';
      const url = `${api}/widget/${this.settings.letsbookApiId}/ConsultaBE`;

      fetch(url)
        .then(async (res) => {
          if (res.status === 200) {
            const destinations = await res.json();

            this.destinations = [
              ...destinations.Destinos.Hoteis,
              ...destinations.Destinos.Cidades,
            ];
          } else if (res.status === 404) {
            console.warn(res);
          } else {
            console.warn('Ops! Algo muito errado aconteceu.');
          }
        })
        .catch((reason) => {
          console.warn(reason);
        });
    },
    addScript: debounce(function addScript() {
      if (!this.scriptExists) {
        const src = `https://widget-be.pmweb.com.br/3.0/pmweb-widget.min.js?v=${Math.floor(
          Math.random() * 10000,
        )}`;
        const script = document.createElement('script');

        script.setAttribute('id', 'editor-widget-script');
        script.setAttribute('type', 'text/javascript');
        script.setAttribute('src', src);
        script.setAttribute('async', 'true');

        (
          document.getElementsByTagName('head')[0] || document.documentElement
        ).appendChild(script);

        this.currentScript = script;
      }
    }, 500),
    removeScript() {
      if (this.scriptExists) {
        this.currentScript.remove();
        this.currentScript = null;
      }
    },
    reloadScript() {
      this.removeScript();
      this.addScript();
    },
    onLayoutChange() {
      this.isMobile = window.innerWidth < 640;
    },
    ...mapActions(['logError']),
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'config',
            label: 'Configurações',
            fields: [
              {
                name: 'useDestinationOrHotel',
                type: 'Checkbox',
                label: 'Definir destino ou hotel padrão',
              },
              {
                name: 'defaultSelectedDestination',
                type: 'VueSelect',
                label: 'Selecione o hotel ou destino',
                isEnabled({ useDestinationOrHotel }) {
                  return useDestinationOrHotel;
                },
                options() {
                  const destinationsToOptions = (destinations) =>
                    destinations.map((destination) => ({
                      label: destination.Nome,
                      value: destination.Codigo,
                    }));

                  if (this.destinations.length > 0) {
                    return destinationsToOptions(this.destinations);
                  }

                  return new Promise((resolve) => {
                    const unwatch = this.$watch(
                      'destinations',
                      (destinations) => {
                        resolve(destinationsToOptions(destinations));
                        unwatch();
                      },
                    );
                  });
                },
              },
              {
                name: 'showPrices',
                type: 'Checkbox',
                label: 'Mostrar preços',
              },
            ],
          },
          {
            name: 'mobile',
            label: 'Mobile',
            fields: [
              {
                name: 'buttonText',
                type: 'ShortText',
                label: 'Texto do botão',
                maxlength: 15,
                validation: {
                  presence: {
                    allowEmpty: false,
                    message: 'Preencha o texto do botão no mobile',
                  },
                },
              },
            ],
          },
        ],
        toRemove: ['text'],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            toRemove: ['titleColor', 'buttonTextColor'],
          },
        ],
        toRemove: ['fonts', 'padding', 'paddingMobile', 'transitions'],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
.widget-light {
  background-color: var(--bg-color);
  display: flex;
  align-items: center;

  @include media-breakpoint-up(lg) {
    position: sticky !important;
    top: -1px;
    z-index: 5000 !important;
  }

  div.pmweb_widget.sweet[id^='pmwebwidget'] {
    font-family: $lb-content-font;
    z-index: 99 !important;

    &.pmweb-light-theme {
      background-color: transparent !important;
      color: var(--text-color);

      ::v-deep {
        .pm_fields .pm_be_block {
          background-color: transparent !important;
        }
        .pm_field_separator {
          background-color: var(--button-color, var(--primary-color));
          opacity: 0.2;
        }
        .pm_my_reservation {
          left: 0;
          margin-left: 17px;

          &:after {
            background-color: var(--button-color, var(--primary-color));
          }
        }
        .pm_input_wrap input,
        .pm_adults .pm_btn_label,
        .pm_children .pm_btn_label,
        .pm_child_age .pm_btn_label,
        .pm_input_promocode .pm_promocode_input {
          color: var(--text-color) !important;
          border-color: var(--text-color) !important;

          &.pm_erro {
            color: red !important;
            border-color: red !important;
          }

          &:hover {
            border-color: var(--button-color, var(--primary-color)) !important;
          }
        }
        .pm_erro ~ .pm_btn_label {
          color: red !important;
          border-color: red !important;
        }
        .pm_input_wrap input {
          &::placeholder {
            // separado pois não pode ter &:hover dentro
            color: var(--text-color) !important;
          }
          &.pm_erro::placeholder {
            color: red !important;
          }
        }
        .pm-promocode-button small {
          color: var(--text-color);
          opacity: 0.8;
        }
        .pm_input_promocode .pm_promocode_input::placeholder {
          color: var(--text-color) !important;
        }
        .pm_child_age_wrap .pm_field_label {
          color: var(--text-color) !important;
        }
      }
    }
  }

  &__mobile {
    padding: 24px 2.5%;
    flex: 1;

    .cta {
      width: 100%;
      background-color: var(--primary-color);
      color: $lb-white;
    }
  }
}

//compensa a margem negativa quando temos menu transparente acima
.menu.absolute + .widget-light {
  margin-top: 90px;
}
</style>
